import { type HeroTypeFront } from '@/src/Types/Blocks/Hero';
import { Buttons } from '@/src/Views/Shared/Buttons/Buttons';
import Html from '@Views/Shared/Html';
import { getTitleWithClass } from '@Views/Shared/lib';

export default function HeroText(props: HeroTypeFront) {
	const {
		id,
		title,
		description,
		image,
		img_position = 'center',
		ctas,
		ctasStyle,
		titleFormat,
		variant,
		containerType,
		contentFontClass,
		context,
		sectionDecoration,
	} = props;

	const attrs: HeroTypeFront = {
		id: id,
		image: image,
		description: description,
		titleFormat: titleFormat,
		title: title,
		ctas: ctas,
		ctasStyle: ctasStyle,
		img_position: img_position,
		contentFontClass: contentFontClass,
		variant: variant,
		containerType: containerType,
		context: context,
		sectionDecoration: sectionDecoration,
	};

	const variants = {
		left: <ImgLeft {...attrs} />,
		right: <ImgRight {...attrs} />,
		center: <ImgCenter {...attrs} />,
		bg: <ImgBG {...attrs} />,
	};

	return <>{variants[img_position]}</>;
}

const ImgLeft = (props: HeroTypeFront) => {
	const {
		image,
		description,
		titleFormat,
		title,
		ctas,
		ctasStyle,
		id,
		contentFontClass,
		containerType,
		context,
	} = props;

	let styleBlock;
	let styleWide;

	if (containerType === 'block') {
		styleBlock = { backgroundImage: `url(${image?.url})` };
		styleWide = {};
	} else {
		styleWide = { backgroundImage: `url(${image?.url})` };
		styleBlock = {};
	}

	return (
		<div
			id={id}
			className={`minos-grid relative ${
				containerType === 'block' ? 'py-6 tablet:py-12' : 'bg-cover bg-center bg-no-repeat'
			}`}
			style={styleWide}
		>
			<div
				className={`col-start-2 col-end-13 grid grid-cols-4 gap-8 sm:col-end-14 tablet:grid-cols-12 ${
					containerType === 'block' &&
					'rounded-minos-hero-container  bg-cover bg-center bg-no-repeat p-6'
				}`}
				style={styleBlock}
				data-aos='fade-up'
			>
				<div
					className='relative col-span-full my-28 overflow-hidden rounded-minos-hero-img bg-minos-hero-bg-secondary-color tablet:col-span-7 tablet:col-start-6 desktop:col-span-6 desktop:col-start-7'
					data-aos='fade-up'
				>
					<div className='bg-minos-hero-bg-secondary-color p-5 tablet:p-12 desktop:p-20'>
						{titleFormat === 'h1' ? (
							<h1 className={`${contentFontClass?.title} text-4xl`}>{getTitleWithClass(title)}</h1>
						) : (
							<h2 className={`${contentFontClass?.title} text-3xl `}>{getTitleWithClass(title)}</h2>
						)}
						<div className='mt-7'>
							<Html context={context} className={contentFontClass?.text} html={description} />
						</div>

						{ctas && <Buttons buttons={ctas} ctasStyle={ctasStyle} context='hero' />}
					</div>
				</div>
			</div>
		</div>
	);
};

const ImgRight = (props: HeroTypeFront) => {
	const {
		image,
		description,
		titleFormat,
		title,
		ctas,
		ctasStyle,
		id,
		contentFontClass,
		containerType,
		context,
	} = props;

	let styleBlock;
	let styleWide;

	if (containerType === 'block') {
		styleBlock = { backgroundImage: `url(${image?.url})` };
		styleWide = {};
	} else {
		styleWide = { backgroundImage: `url(${image?.url})` };
		styleBlock = {};
	}

	return (
		<div
			id={id}
			className={`minos-grid relative ${
				containerType === 'block' ? 'py-6 tablet:py-12' : 'bg-cover bg-center bg-no-repeat'
			}`}
			style={styleWide}
		>
			<div
				className={`col-start-2 col-end-14 ${
					containerType === 'block' && 'rounded-minos-hero-container bg-cover bg-center bg-no-repeat p-6'
				} grid grid-cols-4 gap-8 tablet:grid-cols-12`}
				style={styleBlock}
				data-aos='fade-up'
			>
				<div
					className='col-span-full my-28 overflow-hidden rounded-minos-hero-img bg-minos-hero-bg-secondary-color tablet:col-span-7 desktop:col-span-6 desktop:col-start-2'
					data-aos='fade-up'
				>
					<div className='bg-minos-hero-bg-secondary-color p-5 tablet:p-7 desktop:p-20'>
						{titleFormat === 'h1' ? (
							<h1 className={`${contentFontClass?.title} text-4xl`}>{getTitleWithClass(title)}</h1>
						) : (
							<h2 className={`${contentFontClass?.title} text-3xl `}>{getTitleWithClass(title)}</h2>
						)}
						<div className='mt-7'>
							<Html context={context} className={contentFontClass?.text} html={description} />
						</div>

						{ctas && <Buttons buttons={ctas} ctasStyle={ctasStyle} context='hero' />}
					</div>
				</div>
			</div>
		</div>
	);
};

const ImgCenter = (props: HeroTypeFront) => {
	const {
		image,
		description,
		titleFormat,
		title,
		ctas,
		id,
		contentFontClass,
		context,
		ctasStyle,
		sectionDecoration,
	} = props;

	// no containerType style currently
	// rounded-minos-hero-img --> used for img & wrapper text

	let padding = '';
	if (sectionDecoration?.isSectionDecorated && sectionDecoration.type === 'wave') {
		padding = 'py-20 tablet:pt-20 tablet:pb-32';
	} else {
		padding = 'py-20';
	}
	return (
		<div id={id} className={`minos-grid ${padding}`}>
			<div
				className='col-start-2 col-end-13 flex flex-col items-center justify-center rounded-minos-hero-img bg-cover bg-center bg-no-repeat text-center sm:col-end-14'
				style={{ backgroundImage: `url(${image?.url})` }}
			>
				<div
					className='m-10 overflow-hidden rounded-minos-hero-img bg-minos-hero-bg-secondary-color p-5 tablet:p-7 desktop:m-28 desktop:max-w-[800px] desktop:p-20'
					data-aos='fade-up'
				>
					<>
						{titleFormat === 'h1' ? (
							<h1 className={`${contentFontClass?.title} text-4xl`}>{getTitleWithClass(title)}</h1>
						) : (
							<h2 className={`${contentFontClass?.title} text-3xl `}>{getTitleWithClass(title)}</h2>
						)}
						<div className='mt-7'>
							<Html context={context} className={contentFontClass?.text} html={description} />
						</div>

						{ctas && (
							<Buttons buttons={ctas} ctasStyle={ctasStyle} context='hero' className='justify-center' />
						)}
					</>
				</div>
			</div>
		</div>
	);
};

const ImgBG = (props: HeroTypeFront) => {
	const { image, description, titleFormat, title, ctas, id, contentFontClass, context, ctasStyle } =
		props;
	// no containerType style currently

	return (
		<div
			id={id}
			className='minos-grid relative bg-cover bg-center bg-no-repeat'
			style={{ backgroundImage: `url(${image?.url})` }}
		>
			<div
				className='relative col-start-2 col-end-13 my-28 overflow-hidden text-center sm:col-end-14 tablet:col-start-4 tablet:col-end-12'
				data-aos='fade-up'
			>
				<div className='rounded-minos-hero-img bg-minos-hero-bg-secondary-color p-5 tablet:p-7 desktop:p-20'>
					{titleFormat === 'h1' ? (
						<h1 className={`${contentFontClass?.title} text-4xl`}>{getTitleWithClass(title)}</h1>
					) : (
						<h2 className={`${contentFontClass?.title} text-3xl`}>{getTitleWithClass(title)}</h2>
					)}
					<div className='mt-7'>
						<Html context={context} className={contentFontClass?.text} html={description} />
					</div>

					{ctas && (
						<Buttons buttons={ctas} ctasStyle={ctasStyle} context='hero' className='justify-center' />
					)}
				</div>
			</div>
		</div>
	);
};
